import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react'
import {
  useParams,
  useNavigate,
  useLocation,
  useSearchParams,
} from 'react-router-dom'
import toastr from 'toastr'
import { useMonthCalendarData } from '../../hooks/useMonthCalendarData'
import { getAllItemsForDay } from '../../utils/calendarHelpers'
import { PANEL_ANIMATION_DURATION_MS } from '../../constants/animations'
import PageLayout from '../common/layout/PageLayout'
import MonthCalendarView from './MonthCalendarView'
import MonthCalendarSkeleton from './MonthCalendarSkeleton'
import DayDetailPanel from './DayDetailPanel'
import AvailabilityModal from './AvailabilityModal'
import MatchProposalModal from './MatchProposalModal'
import ProposalActionModal from './ProposalActionModal'
import AvailabilityAddButton from './AvailabilityAddButton'
import ViewToggle from './ViewToggle'
import './MonthCalendarPage.css'

// Default hour for availability modal when creating new availability
const DEFAULT_AVAILABILITY_HOUR = 9
const DEFAULT_AVAILABILITY_MINUTE = 0

function MonthCalendarPage() {
  const { leagueId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()

  // Get data from the custom hook
  const {
    availabilityByDate,
    opponentAvailabilityByDate,
    unplayedOpponents,
    divisionInfo,
    proposalsByDate,
    matchesByDate,
    loading,
    handleAvailabilitySave,
    handleAvailabilityDelete,
    handleMatchProposalConfirm,
    handleProposalAction,
  } = useMonthCalendarData()

  // UI state
  const [selectedDate, setSelectedDate] = useState(null)
  const [isClosing, setIsClosing] = useState(false)
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false)
  const [availabilityModalMode, setAvailabilityModalMode] = useState('create')
  const [selectedAvailability, setSelectedAvailability] = useState(null)

  // Capture openAvailabilityModal flag from navigation state or query param on mount
  useEffect(() => {
    if (
      location.state?.openAvailabilityModal ||
      searchParams.get('openAvailability') === 'true'
    ) {
      shouldOpenModalRef.current = true
    }
  }, [location.state, searchParams])
  const [proposalModalOpen, setProposalModalOpen] = useState(false)
  const [selectedProposal, setSelectedProposal] = useState(null)
  const [proposalActionModalOpen, setProposalActionModalOpen] = useState(false)
  const [selectedProposalForAction, setSelectedProposalForAction] =
    useState(null)

  // Ref to store the last focused element before opening the panel
  const lastFocusedElementRef = useRef(null)

  // Ref to preserve the openAvailabilityModal flag across navigations
  const shouldOpenModalRef = useRef(false)

  // Memoize default hour object to prevent unnecessary re-renders
  const defaultHourObject = useMemo(
    () => ({
      hour: DEFAULT_AVAILABILITY_HOUR,
      minute: DEFAULT_AVAILABILITY_MINUTE,
    }),
    [],
  )

  // Convert data to arrays for MonthCalendarView
  const availability = useMemo(() => {
    if (!availabilityByDate) return []
    const items = []
    Object.values(availabilityByDate).forEach((value) => {
      items.push(...value)
    })
    return items
  }, [availabilityByDate])

  const opponentAvailability = useMemo(() => {
    if (!opponentAvailabilityByDate) return []
    const items = []
    Object.values(opponentAvailabilityByDate).forEach((value) => {
      items.push(...value)
    })
    return items
  }, [opponentAvailabilityByDate])

  const proposals = useMemo(() => {
    if (!proposalsByDate) return []
    const items = []
    Object.values(proposalsByDate).forEach((value) => {
      items.push(...value)
    })
    return items
  }, [proposalsByDate])

  const matches = useMemo(() => {
    if (!matchesByDate) return []
    const items = []
    Object.values(matchesByDate).forEach((value) => {
      items.push(...value)
    })
    return items
  }, [matchesByDate])

  // Get items for selected date
  const selectedDateItems = useMemo(() => {
    if (!selectedDate)
      return {
        availability: [],
        opponentAvailability: [],
        proposals: [],
        matches: [],
      }
    return getAllItemsForDay(
      selectedDate,
      availabilityByDate,
      proposalsByDate,
      matchesByDate,
      opponentAvailabilityByDate,
    )
  }, [
    selectedDate,
    availabilityByDate,
    opponentAvailabilityByDate,
    proposalsByDate,
    matchesByDate,
  ])

  // Create screen reader announcement for selected date
  const selectedDateAnnouncement = useMemo(() => {
    if (!selectedDate) return ''
    const dateStr = selectedDate.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })

    const items = selectedDateItems
    const totalItems =
      items.availability.length +
      items.opponentAvailability.length +
      items.proposals.length +
      items.matches.length
    return `Selected ${dateStr}. ${totalItems} ${
      totalItems === 1 ? 'item' : 'items'
    } for this day.`
  }, [selectedDate, selectedDateItems])

  // Handle day click from MonthCalendarView
  const handleDayClick = useCallback((date) => {
    lastFocusedElementRef.current = document.activeElement
    setSelectedDate(date)
  }, [])

  // Open availability modal if navigated from league landing
  useEffect(() => {
    if (shouldOpenModalRef.current && !loading) {
      setSelectedDate(new Date())
      setAvailabilityModalMode('create')
      setSelectedAvailability(null)
      setAvailabilityModalOpen(true)
      // Clear the ref so modal doesn't reopen
      shouldOpenModalRef.current = false
    }
  }, [loading])

  // Handle add availability — can be called from DayDetailPanel (no arg) or
  // from the page header button (passes a fallback date like new Date())
  const handleAddAvailability = useCallback(
    (date) => {
      const dateToUse = date instanceof Date ? date : selectedDate
      if (!dateToUse) return
      if (date instanceof Date) {
        setSelectedDate(dateToUse)
      }
      setAvailabilityModalMode('create')
      setSelectedAvailability(null)
      setAvailabilityModalOpen(true)
    },
    [selectedDate],
  )

  // Handle edit availability from DayDetailPanel
  const handleEditAvailability = useCallback(
    (availabilityId) => {
      const avail = selectedDateItems.availability.find(
        (a) => a.id === availabilityId,
      )
      if (!avail) return
      setAvailabilityModalMode('edit')
      setSelectedAvailability(avail)
      setAvailabilityModalOpen(true)
    },
    [selectedDateItems.availability],
  )

  // Handle opponent availability click - propose a match at that time
  const handleOpponentAvailabilityClick = useCallback(
    (opponentAvailability) => {
      // Find the opponent in the unplayedOpponents list
      const opponent = unplayedOpponents.find(
        (opp) => opp.user_id === opponentAvailability.opponent_user_id,
      )
      if (!opponent) {
        toastr.error('Opponent not found')
        return
      }

      // Format the opponent object for the modal
      const formattedOpponent = {
        id: opponent.player_id || opponent.id,
        first_name: opponent.first_name,
        last_name: opponent.last_name,
      }

      // Set the proposed time to the start of the opponent's availability
      setSelectedProposal({
        opponent: formattedOpponent,
        proposedTime: opponentAvailability.start_time,
        availabilityStart: opponentAvailability.start_time,
        availabilityEnd: opponentAvailability.end_time,
      })
      setProposalModalOpen(true)
    },
    [unplayedOpponents],
  )

  // Handle proposal action from DayDetailPanel
  const handleProposalClick = useCallback((proposal) => {
    setSelectedProposalForAction(proposal)
    setProposalActionModalOpen(true)
  }, [])

  // Extract opponent from selected proposal
  const selectedProposalOpponent = useMemo(() => {
    if (!selectedProposalForAction) return null

    // Extract opponent ID based on proposal direction
    const opponentId = selectedProposalForAction.is_sent_by_user
      ? selectedProposalForAction.opponent_player_id
      : selectedProposalForAction.proposer_player_id

    // Try to get opponent name from proposal
    const opponentName = selectedProposalForAction.is_sent_by_user
      ? selectedProposalForAction.opponent_name
      : selectedProposalForAction.proposer_name

    if (opponentName && typeof opponentName === 'string') {
      const nameParts = opponentName.split(' ')
      return {
        id: opponentId,
        first_name: nameParts[0] || '',
        last_name: nameParts.slice(1).join(' ') || '',
      }
    }

    // Fallback if no name available
    return {
      id: opponentId,
      first_name: 'Unknown',
      last_name: 'Player',
    }
  }, [selectedProposalForAction])

  // Handle match click from DayDetailPanel
  const handleMatchClick = useCallback(
    (match) => {
      navigate(`/leagues/${leagueId}/match/${match.id}/score`)
    },
    [navigate, leagueId],
  )

  // Close DayDetailPanel
  const handleCloseDayDetail = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setSelectedDate(null)
      setIsClosing(false)
      // Restore focus to the day cell
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.focus()
      }
    }, PANEL_ANIMATION_DURATION_MS)
  }, [])

  // Close AvailabilityModal
  const handleCloseAvailabilityModal = useCallback(() => {
    setAvailabilityModalOpen(false)
    setAvailabilityModalMode('create')
    setSelectedAvailability(null)
  }, [])

  // Close ProposalModal
  const handleCloseProposalModal = useCallback(() => {
    setProposalModalOpen(false)
    setSelectedProposal(null)
  }, [])

  // Close ProposalActionModal
  const handleCloseProposalActionModal = useCallback(() => {
    setProposalActionModalOpen(false)
    setSelectedProposalForAction(null)
  }, [])

  // Save availability wrapper
  const handleAvailabilitySaveWrapper = useCallback(
    async (availabilityData) => {
      try {
        await handleAvailabilitySave(availabilityData, availabilityModalMode)
        handleCloseAvailabilityModal()
        toastr.success(
          availabilityData.apply_to === 'team'
            ? 'Team availability updated.'
            : 'Your availability saved.',
        )
      } catch (error) {
        console.error('Failed to save availability:', error)
        // Provide user feedback if service layer didn't handle it
        toastr.error(
          'Unable to save availability. Please check your connection and try again.',
        )
        // Keep the modal open so user can retry
      }
    },
    [
      handleAvailabilitySave,
      availabilityModalMode,
      handleCloseAvailabilityModal,
    ],
  )

  // Delete availability wrapper
  const handleAvailabilityDeleteWrapper = useCallback(
    async (availabilityId, exceptionDate = null, options = {}) => {
      try {
        await handleAvailabilityDelete(availabilityId, exceptionDate, options)
        handleCloseAvailabilityModal()
      } catch (error) {
        console.error('Failed to delete availability:', error)
        // Provide user feedback if service layer didn't handle it
        toastr.error(
          'Unable to delete availability. Please check your connection and try again.',
        )
        // Keep the modal open so user can retry
      }
    },
    [handleAvailabilityDelete, handleCloseAvailabilityModal],
  )

  const isDoublesLeague = divisionInfo?.league_type === 'Doubles'
  const currentPlayer = (divisionInfo?.roster || []).find(
    (player) => Number(player.id) === Number(divisionInfo?.player_id),
  )
  const currentTeamId = currentPlayer?.partner?.team_id || null

  // Proposal action wrapper
  const handleProposalActionWrapper = useCallback(
    async (action, proposal) => {
      try {
        await handleProposalAction(action, proposal)
        handleCloseProposalActionModal()
      } catch (error) {
        console.error('Failed to handle proposal action:', error)
        // Provide user feedback if service layer didn't handle it
        const actionText = action === 'accept' ? 'accept' : 'decline'
        toastr.error(
          `Unable to ${actionText} proposal. Please check your connection and try again.`,
        )
        // Keep the modal open so user can retry
      }
    },
    [handleProposalAction, handleCloseProposalActionModal],
  )

  if (loading) {
    return (
      <PageLayout title="Month Calendar">
        <div className="fl__month-calendar-page">
          <div className="fl__month-calendar-page__header">
            <ViewToggle currentView="month" />
            <AvailabilityAddButton
              className="fl__month-calendar-page__add-button"
              onClick={() => handleAddAvailability(new Date())}
              testId="month-calendar-add-availability-button"
            />
          </div>
          <MonthCalendarSkeleton />
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Month Calendar">
      <div className="fl__month-calendar-page">
        {/* Screen reader announcements for date changes */}
        <div
          role="status"
          aria-live="polite"
          aria-atomic="true"
          className="sr-only"
        >
          {selectedDateAnnouncement}
        </div>

        {/* View Toggle with Add Button */}
        <div className="fl__month-calendar-page__header">
          <ViewToggle currentView="month" />
          <AvailabilityAddButton
            className="fl__month-calendar-page__add-button"
            onClick={() => handleAddAvailability(new Date())}
            testId="month-calendar-add-availability-button"
          />
        </div>

        {/* Month Calendar View */}
        <MonthCalendarView
          availability={availability}
          opponentAvailability={opponentAvailability}
          proposals={proposals}
          matches={matches}
          onDayClick={handleDayClick}
          selectedDate={selectedDate}
        />

        {/* Day Detail Panel */}
        {selectedDate && (
          <DayDetailPanel
            selectedDate={selectedDate}
            isClosing={isClosing}
            availability={selectedDateItems.availability}
            opponentAvailability={selectedDateItems.opponentAvailability}
            unplayedOpponents={unplayedOpponents}
            proposals={selectedDateItems.proposals}
            matches={selectedDateItems.matches}
            onClose={handleCloseDayDetail}
            onAddAvailability={handleAddAvailability}
            onEditAvailability={handleEditAvailability}
            onOpponentAvailabilityClick={handleOpponentAvailabilityClick}
            onProposalAction={handleProposalClick}
            onMatchClick={handleMatchClick}
          />
        )}

        {/* Availability Modal */}
        <AvailabilityModal
          isOpen={availabilityModalOpen}
          mode={availabilityModalMode}
          selectedDate={selectedDate}
          selectedHour={selectedDate ? defaultHourObject : null}
          existingAvailability={selectedAvailability}
          onClose={handleCloseAvailabilityModal}
          onSave={handleAvailabilitySaveWrapper}
          onDelete={handleAvailabilityDeleteWrapper}
          leagueId={divisionInfo?.league_id}
          divisionId={divisionInfo?.division_id}
          teamId={currentTeamId}
          isDoublesLeague={isDoublesLeague}
          leagueEndDate={divisionInfo?.league_end_date}
        />

        {/* Match Proposal Modal */}
        <MatchProposalModal
          isOpen={proposalModalOpen && selectedProposal !== null}
          onClose={handleCloseProposalModal}
          opponent={selectedProposal?.opponent}
          proposedTime={selectedProposal?.proposedTime}
          availabilityStart={selectedProposal?.availabilityStart}
          availabilityEnd={selectedProposal?.availabilityEnd}
          leagueId={leagueId}
          onConfirm={handleMatchProposalConfirm}
        />

        {/* Proposal Action Modal */}
        <ProposalActionModal
          isOpen={proposalActionModalOpen && selectedProposalForAction !== null}
          onClose={handleCloseProposalActionModal}
          proposal={selectedProposalForAction}
          opponent={selectedProposalOpponent}
          onAction={handleProposalActionWrapper}
        />
      </div>
    </PageLayout>
  )
}

export default MonthCalendarPage
